// Blocos dos indicadores novos do Financeiro e do Faturamento (#6506), encaixados nas telas
// da Cobrança, do Pós-venda, da Gestão do Financeiro e do Backoffice.
import React, { useMemo } from 'react';
import { diasUteisEntre } from '../../src/lib/reguaPrazosAgrupamento';
import { hojeIsoLocal } from '../../src/lib/conferenciaFaturamentoDb';
import {
  ESPECIALIDADE_POR_PESSOA, META_ACORDOS_CUMPRIDOS, META_REGUA_EM_DIA,
  acordosCumpridos, boletosAEnviar, enviosEmAte1DiaUtil, reguaEmDia,
} from '../../src/lib/visaoGeral/cobranca';
import { entraESaiDoRateio } from '../../src/lib/visaoGeral/backoffice';
import { FILAS } from '../Financeiro/EsteiraCobranca/filas';
import { CartaoHoje, CartaoIndicador, PainelDetalhe, plural } from './componentes';
import {
  useAcoesDaReguaDoPeriodo, useBoletosAEnviar, useDocumentosDaEsteira, useEntraESaiDoRateio, useEnviosDaPessoa, useParcelasDosMeusAcordos,
} from './useIndicadoresFase2';

const LISTA = 8;
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const uteis = (de: string, ate: string) => diasUteisEntre(de, ate);

// ─────────────────────────── Régua em dia e acordos cumpridos ───────────────────────────

/** Os dois indicadores do mês de quem cobra: régua em dia (opcional) e acordos cumpridos. */
export const CartoesReguaEAcordos: React.FC<{ pessoaId: string; inicioIso: string; fimIso: string; hojeIso: string; rotuloMes: string; comRegua?: boolean }> = ({ pessoaId, inicioIso, fimIso, hojeIso, rotuloMes, comRegua = true }) => {
  const acoes = useAcoesDaReguaDoPeriodo(pessoaId, inicioIso);
  const parcelas = useParcelasDosMeusAcordos(pessoaId, inicioIso, fimIso);
  const regua = useMemo(() => (acoes.dados ? reguaEmDia(acoes.dados, pessoaId, inicioIso, fimIso, hojeIso) : null), [acoes.dados, pessoaId, inicioIso, fimIso, hojeIso]);
  const acordos = useMemo(() => (parcelas.dados ? acordosCumpridos(parcelas.dados, inicioIso, fimIso, hojeIso) : null), [parcelas.dados, inicioIso, fimIso, hojeIso]);
  return (
    <>
      {comRegua && (
        <CartaoIndicador
          titulo="Régua em dia"
          valor={regua ? (regua.taxa == null ? '—' : pct(regua.taxa)) : null}
          meta={regua?.taxa != null ? { texto: `meta ${pct(META_REGUA_EM_DIA)}`, atingido: regua.taxa / META_REGUA_EM_DIA } : undefined}
          detalhe={regua ? `${regua.emDia} de ${regua.devidas} ações de ${rotuloMes} feitas até o dia previsto. ${regua.feitasComAtraso} feitas depois do dia e ${regua.pendentes} ainda pendentes.` : undefined}
          para="/financeiro/lembretes-cobranca"
          origem="Ação da régua (D+4, D+10, D+17, D+24) da sua carteira feita, ou dispensada com motivo, até o dia previsto (vencimento do boleto + N). A do dia de hoje ainda pendente não conta."
        />
      )}
      <CartaoIndicador
        titulo="Acordos cumpridos"
        valor={acordos ? (acordos.taxa == null ? '—' : pct(acordos.taxa)) : null}
        meta={acordos?.taxa != null ? { texto: `meta ${pct(META_ACORDOS_CUMPRIDOS)}`, atingido: acordos.taxa / META_ACORDOS_CUMPRIDOS } : undefined}
        detalhe={acordos ? (acordos.vencidas
          ? `${acordos.emDia} de ${acordos.vencidas} parcelas dos seus acordos que venceram em ${rotuloMes} pagas até o vencimento. ${acordos.pagasComAtraso} pagas com atraso e ${acordos.emAberto} em aberto.`
          : `Nenhuma parcela dos seus acordos venceu em ${rotuloMes} até ontem.`) : undefined}
        para="/financeiro/parcelamentos"
        origem="Parcelas (inclusive a entrada) dos acordos que você fechou, pela data de pagamento do boleto. Parcela cancelada não conta."
      />
    </>
  );
};

// ─────────────────────────── Especialidade da Cobrança ───────────────────────────

export function useEspecialidade(pessoaId: string, nome: string | null, inicio: string, fim: string) {
  const especialidade = ESPECIALIDADE_POR_PESSOA[pessoaId] ?? null;
  const boletos = useBoletosAEnviar();
  const envios = useEnviosDaPessoa(especialidade === 'envio_boletos' ? nome : null, inicio, fim);
  const documentos = useDocumentosDaEsteira();
  const hojeIso = hojeIsoLocal();
  const fila = useMemo(() => (boletos.dados ? boletosAEnviar(boletos.dados, hojeIso, uteis) : null), [boletos.dados, hojeIso]);
  const taxaEnvio = useMemo(() => (envios.dados ? enviosEmAte1DiaUtil(envios.dados, uteis) : null), [envios.dados]);
  const docs = useMemo(() => (documentos.dados
    ? [...documentos.dados].sort((a, b) => (a.prazo_em ?? '9999').localeCompare(b.prazo_em ?? '9999') || b.dias_parado - a.dias_parado)
    : null), [documentos.dados]);
  const docsVencidos = docs?.filter(d => d.prazo_em && d.prazo_em.slice(0, 10) < hojeIso).length ?? 0;
  // Quase nenhum documento tem prazo gravado (o distrato não tem): parado há mais de 30 dias também acende.
  const docsParados = docs?.filter(d => !(d.prazo_em && d.prazo_em.slice(0, 10) < hojeIso) && d.dias_parado > 30).length ?? 0;
  return { especialidade, fila, taxaEnvio, docs, docsVencidos, docsParados };
}

export const CartaoEspecialidade: React.FC<{ e: ReturnType<typeof useEspecialidade> }> = ({ e }) => {
  if (e.especialidade === 'envio_boletos') {
    const parados = e.fila?.filter(b => b.diasUteis > 1).length ?? 0;
    return (
      <CartaoHoje valor={e.fila ? e.fila.length : null} rotulo="boletos emitidos esperando envio ao cliente" tom={parados ? 'critico' : 'atencao'} para="/financeiro/faturas"
        dica={e.fila ? `${parados} esperam há mais de 1 dia útil. Sua especialidade: enviar ao cliente em até 1 dia útil depois da emissão.` : undefined} />
    );
  }
  if (e.especialidade === 'documentos') {
    return (
      <CartaoHoje valor={e.docs ? e.docs.length : null} rotulo="notificações, documentos e negativações" tom={e.docsVencidos || e.docsParados ? 'critico' : 'atencao'} para="/financeiro/esteira"
        dica={e.docs ? `${e.docsVencidos} com o prazo já vencido e ${e.docsParados} parados há mais de 30 dias. Sua especialidade: notificação, negativação e distrato no prazo.` : undefined} />
    );
  }
  return null;
};

export const PainelEspecialidade: React.FC<{ e: ReturnType<typeof useEspecialidade>; rotuloMes: string }> = ({ e, rotuloMes }) => {
  if (e.especialidade === 'envio_boletos') {
    return (
      <PainelDetalhe titulo="Boletos esperando envio" acao={{ rotulo: 'Faturas e Cobranças', para: '/financeiro/faturas' }}>
        {!e.fila ? <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" /> : e.fila.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum boleto emitido esperando envio.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {e.fila.slice(0, LISTA).map(b => (
              <li key={b.id} className="flex items-start gap-3 py-2.5">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{b.cliente_nome || 'Sem nome'}</span>
                  <span className="block text-xs text-gray-400">UC {b.uc_codigo} · {b.mes_referencia} · emitido em {dataCurta(b.emitido_em.slice(0, 10))}</span>
                </span>
                <span className={`shrink-0 text-xs font-semibold ${b.diasUteis > 1 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {b.diasUteis === 0 ? 'hoje' : plural(b.diasUteis, 'dia útil', 'dias úteis')}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-400 mt-3">
          Do mais antigo para o mais novo (sem as UCs da cobrança agrupada da Prefeitura).
          {e.taxaEnvio?.taxa != null ? ` Em ${rotuloMes}, ${pct(e.taxaEnvio.taxa)} dos ${e.taxaEnvio.enviados} boletos que você enviou saíram em até 1 dia útil.` : ''}
          {e.fila && e.fila.length > LISTA ? ` Mais ${plural(e.fila.length - LISTA, 'boleto', 'boletos')}.` : ''}
        </p>
      </PainelDetalhe>
    );
  }
  if (e.especialidade === 'documentos') {
    return (
      <PainelDetalhe titulo="Notificações, documentos e negativações" acao={{ rotulo: 'Esteira', para: '/financeiro/esteira' }}>
        {!e.docs ? <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" /> : e.docs.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nada pendente nas filas de documento.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {Object.entries(e.docs.reduce<Record<string, number>>((m, d) => { m[d.fila] = (m[d.fila] ?? 0) + 1; return m; }, {}))
                .sort((a, b) => b[1] - a[1]).map(([fila, n]) => (
                  <span key={fila} className="text-xs rounded-full px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {FILAS[fila as keyof typeof FILAS]?.titulo ?? fila}: {n}
                  </span>
                ))}
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {e.docs.slice(0, LISTA).map(d => {
                const vencido = !!d.prazo_em && d.prazo_em.slice(0, 10) < hojeIsoLocal();
                return (
                  <li key={d.id} className="flex items-start gap-3 py-2.5">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{d.cliente_nome || 'Sem nome'}</span>
                      <span className="block text-xs text-gray-400 truncate">{FILAS[d.fila]?.titulo ?? d.fila}</span>
                    </span>
                    <span className={`shrink-0 text-right text-xs font-semibold ${vencido ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {d.prazo_em ? `${vencido ? 'prazo venceu' : 'prazo'} ${dataCurta(d.prazo_em.slice(0, 10))}` : `parado há ${plural(d.dias_parado, 'dia', 'dias')}`}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-400 mt-3">
              Filas de notificação (D+17 e mora), documentos (advertência e distrato) e negativação da Esteira de Cobrança, com prazo mais próximo primeiro.
              {e.docs.length > LISTA ? ` Mais ${plural(e.docs.length - LISTA, 'pendência', 'pendências')} na Esteira.` : ''}
            </p>
          </>
        )}
      </PainelDetalhe>
    );
  }
  return null;
};

// ─────────────────────────── Quem entra e quem sai do rateio ───────────────────────────

export function useEntraESai() {
  const base = useEntraESaiDoRateio();
  const hojeIso = hojeIsoLocal();
  const r = useMemo(() => (base.dados ? entraESaiDoRateio(base.dados.itens, base.dados.aprovadas, base.dados.cancelamentos, hojeIso) : null), [base.dados, hojeIso]);
  return { planilha: base.dados?.planilha ?? null, r, erro: base.erro };
}

export const PainelEntraESai: React.FC<{ e: ReturnType<typeof useEntraESai>; reincluir: number | null }> = ({ e, reincluir }) => (
  <PainelDetalhe titulo="Quem entra e quem sai do rateio" acao={{ rotulo: 'Rateio Antigo', para: '/gestao/rateio-antigo' }}>
    {e.erro ? <p className="text-sm text-red-600 dark:text-red-400">Não foi possível montar a lista.</p> : !e.r ? (
      <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
    ) : (
      <>
        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2 py-2"><span className="block text-lg font-bold text-amber-700 dark:text-amber-300">{e.r.aprovadasFora.length}</span><span className="block text-[11px] text-gray-500">aprovadas fora</span></div>
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 px-2 py-2"><span className="block text-lg font-bold text-red-700 dark:text-red-300">{e.r.canceladasDentro.length}</span><span className="block text-[11px] text-gray-500">canceladas dentro</span></div>
          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 px-2 py-2"><span className="block text-lg font-bold text-gray-800 dark:text-gray-100">{reincluir ?? '…'}</span><span className="block text-[11px] text-gray-500">para voltar</span></div>
        </div>
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {e.r.canceladasDentro.map(c => (
            <li key={`c${c.id}`} className="flex items-start gap-3 py-2.5">
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{c.cliente || 'Sem nome'}</span>
                <span className="block text-xs text-gray-400">UC {c.uc_numero} · cancelamento concluído em {dataCurta(c.concluida_em?.slice(0, 10))}</span>
              </span>
              <span className="shrink-0 text-right text-xs font-semibold text-red-600 dark:text-red-400">sai<span className="block font-normal text-gray-500">"{c.statusNaPlanilha}" na planilha</span></span>
            </li>
          ))}
          {e.r.aprovadasFora.slice(0, LISTA).map(a => (
            <li key={`a${a.negociacao_id}`} className="flex items-start gap-3 py-2.5">
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{a.cliente || 'Sem nome'}</span>
                <span className="block text-xs text-gray-400">aprovada em {dataCurta(a.aprovadaEm.slice(0, 10))}{a.consumo_kwh ? ` · ${Math.round(a.consumo_kwh).toLocaleString('pt-BR')} kWh` : ''}</span>
              </span>
              <span className={`shrink-0 text-right text-xs font-semibold ${a.dias > 30 ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>entra<span className="block font-normal text-gray-500">há {plural(a.dias, 'dia', 'dias')}</span></span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-400 mt-3">
          Contra a planilha de {e.planilha}. "Aprovadas fora": negociação aprovada nos últimos 6 meses sem nenhuma UC na planilha (entra no próximo envio).
          "Canceladas dentro": cancelamento concluído com a UC ainda recebendo energia (sai no próximo envio). "Para voltar": UCs fora do rateio com saldo para menos de 2 meses (painel de Saldos).
          {e.r.aprovadasFora.length > LISTA ? ` Mais ${plural(e.r.aprovadasFora.length - LISTA, 'aprovada', 'aprovadas')} fora.` : ''}
        </p>
      </>
    )}
  </PainelDetalhe>
);
